import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAvailableThemes, getRestaurant, updateRestaurantTheme } from '../../services/api';
import { toast } from '../../lib/toast';
import AppShell from '../../components/layout/AppShell';
import './ThemeSettings.css';

const ThemeSettings = () => {
  const { restaurantId } = useParams();
  const { t } = useTranslation();

  const [themes, setThemes] = useState([]);
  const [currentThemeId, setCurrentThemeId] = useState('classic');
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [themesRes, restaurantRes] = await Promise.all([
          getAvailableThemes(),
          getRestaurant(restaurantId),
        ]);
        const themesData = themesRes.data?.themes || [];
        const restaurant = restaurantRes.data || {};
        setThemes(themesData);
        const themeId = restaurant.theme_id || 'classic';
        setCurrentThemeId(themeId);
        setSelectedTheme(themeId);
      } catch (error) {
        console.error('Error fetching themes:', error);
        toast.error(t('themes.errors.fetchFailed', 'Failed to load themes'));
      } finally {
        setLoading(false);
      }
    };
    if (restaurantId) fetchData();
  }, [restaurantId, t]);

  const handleSaveTheme = async () => {
    if (selectedTheme === currentThemeId) {
      toast.success(t('themes.noChanges', 'No changes to save'));
      return;
    }
    setSaving(true);
    try {
      await updateRestaurantTheme(restaurantId, selectedTheme, null);
      setCurrentThemeId(selectedTheme);
      toast.success(t('themes.saveSuccess', 'Theme updated successfully!'));
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.error(t('themes.errors.saveFailed', 'Failed to save theme'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (themeId) => {
    const previewUrl = `${window.location.origin}/menu/${restaurantId}?preview=true&theme=${themeId}`;
    window.open(previewUrl, '_blank');
  };

  if (loading) {
    return (
      <AppShell maxWidth={false}>
        <div className="theme-settings-loading">
          <div className="loading"></div>
          <p>{t('common.loading', 'Loading...')}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth={false}>
    <div className="theme-settings-page">
      <div className="theme-settings-header">
        <div>
          <h1>{t('themes.title', 'Menu Theme Settings')}</h1>
          <p className="subtitle">
            {t('themes.subtitle', "Choose a theme that matches your restaurant's brand")}
          </p>
        </div>
        <div className="theme-settings-actions">
          <Link to="/manager/dashboard" className="btn btn-secondary">
            {t('common.back', 'Back')}
          </Link>
          {selectedTheme !== currentThemeId && (
            <button
              className="btn-save-theme"
              onClick={handleSaveTheme}
              disabled={saving}
            >
              {saving ? t('common.saving', 'Saving...') : t('themes.saveTheme', 'Save Theme')}
            </button>
          )}
        </div>
      </div>

      <div className="current-theme-info">
        <span className="current-theme-label">
          {t('themes.currentTheme', 'Current theme')}:
        </span>
        <span className="current-theme-name">
          {themes.find((th) => th.id === currentThemeId)?.name || currentThemeId}
        </span>
      </div>

      <div className="themes-grid">
        {themes.map((theme) => (
          <div
            key={theme.id}
            className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''} ${currentThemeId === theme.id ? 'current' : ''}`}
            onClick={() => setSelectedTheme(theme.id)}
          >
            {currentThemeId === theme.id && (
              <div className="current-badge">
                ✓ {t('themes.active', 'Active')}
              </div>
            )}

            <div
              className="theme-preview"
              style={{
                background: `linear-gradient(135deg, ${theme.color_primary} 0%, ${theme.color_secondary} 100%)`,
              }}
            >
              <div className="theme-preview-content">
                <div
                  className="preview-card"
                  style={{
                    backgroundColor: theme.color_background,
                    color: theme.color_text,
                    borderColor: theme.color_accent,
                  }}
                >
                  <div className="preview-text" style={{ fontFamily: theme.font_family }}>
                    {t('themes.previewSample.menuItem')}
                  </div>
                  <div
                    className="preview-button"
                    style={{
                      backgroundColor: theme.color_accent,
                      color: theme.color_background,
                    }}
                  >
                    {t('themes.previewSample.addToCart')}
                  </div>
                </div>
              </div>
            </div>

            <div className="theme-info">
              <h3 className="theme-name">{theme.name}</h3>
              <p className="theme-description">{theme.description}</p>

              <div className="theme-colors">
                <div
                  className="color-swatch"
                  style={{ backgroundColor: theme.color_primary }}
                  title={t('themes.colors.primary', 'Primary')}
                />
                <div
                  className="color-swatch"
                  style={{ backgroundColor: theme.color_secondary }}
                  title={t('themes.colors.secondary', 'Secondary')}
                />
                <div
                  className="color-swatch"
                  style={{ backgroundColor: theme.color_accent }}
                  title={t('themes.colors.accent', 'Accent')}
                />
                <div
                  className="color-swatch"
                  style={{ backgroundColor: theme.color_background, border: '1px solid #e0e0e0' }}
                  title={t('themes.colors.background', 'Background')}
                />
              </div>

              <div className="theme-actions">
                <button
                  type="button"
                  className="btn-preview"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(theme.id);
                  }}
                >
                  {t('themes.preview', 'Preview Menu')}
                </button>
                {selectedTheme === theme.id && selectedTheme !== currentThemeId && (
                  <button
                    type="button"
                    className="btn-select"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveTheme();
                    }}
                  >
                    {t('themes.select', 'Apply This Theme')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="theme-help">
        <h3>{t('themes.help.title', 'How Theme Selection Works')}</h3>
        <ul>
          <li>{t('themes.help.point1', 'Click on a theme to select it')}</li>
          <li>{t('themes.help.point2', 'Use "Preview Menu" to see how your menu will look')}</li>
          <li>{t('themes.help.point3', 'Click "Save Theme" to apply the selected theme')}</li>
          <li>{t('themes.help.point4', 'Your customers will see the new theme when they scan the QR code')}</li>
        </ul>
      </div>
    </div>
    </AppShell>
  );
};

export default ThemeSettings;
